// Collapsible UI panes: Controls (left), Colorbar (right),
// Title/Info (top-left), Coordinate (bottom). All inherit the unified `.pane`
// CSS primitive — drag, magnetic snap, edge-aware chrome — via setupPane.
// Performance/FPS is a visual-only pane and is not wired here.

import { INTENTS } from '../store/intents';
import type { Store } from '../store/store';
import type { State } from '../store/schema';
import type { ViewerContext } from '../app/context';
import { makeDraggable, installResizeClamp, installSizeClamp, raisePane, resolveCollapsedOverlap, type PaneEdge, type DragHandle } from './draggablePanels';

type Which = 'controls' | 'colorbar';

interface ToggleOpts {
    onAfterResize?: (() => void) | undefined;
}

// Collapse-state feedback comes from the .pane CSS width/height + chevron
// transitions; no opacity flash needed (it would collide with the cold-start
// `opacity 0.6s` baked into .pane).
export function togglePanel(panel: HTMLElement, opts: ToggleOpts = {}): void {
    panel.classList.toggle('collapsed');
    if (opts.onAfterResize) setTimeout(opts.onAfterResize, 300);
    // Expand-open raises to top; collapse does not. Mirrors the click path.
    if (!panel.classList.contains('collapsed')) raisePane(panel);
    // On collapse: the just-collapsed pane may now overlap another collapsed
    // pane (CSS pulls side panels to top:15, matching other docked buttons).
    // Wait for the 0.3s width/height transition before resolving so offset
    // dimensions reflect the collapsed shape.
    else setTimeout(() => resolveCollapsedOverlap(panel), 310);
}

export interface SetupPaneOpts {
    panel: HTMLElement;
    /** Element from which drag is initiated. Defaults to the first `.pane-header`
     *  inside `panel`, falling back to `panel` itself. */
    dragHandle?: HTMLElement | undefined;
    onAfterResize?: (() => void) | undefined;
    onEdgeChange?: ((edge: PaneEdge) => void) | undefined;
    onToggle?: ((collapsedNow: boolean) => void) | undefined;
    /** When true, drag still works but header-click and pane-toggle-button
     *  clicks do NOT collapse the pane. Use for panes whose only dismiss
     *  affordance is an explicit close button (e.g. Layers). */
    disableCollapse?: boolean;
}

/** Wire drag + click-toggle + edge-change for a pane. Returns the drag handle. */
export function setupPane(opts: SetupPaneOpts): DragHandle {
    const { panel, onAfterResize, onEdgeChange, onToggle } = opts;
    const handle = opts.dragHandle
        ?? (panel.querySelector('.pane-header') as HTMLElement | null)
        ?? panel;
    const drag = makeDraggable(panel, { dragHandle: handle });
    // Auto-fit to viewport whenever the pane's size changes (collapse,
    // expand, content reflow). Debounce so the clamp runs once after the
    // CSS transition settles, not every frame.
    installSizeClamp(panel);

    // Any pointer interaction with the pane lifts it above other recently
    // interacted panes. Capture phase so it fires even when descendants
    // (e.g. pane internals) stop propagation.
    panel.addEventListener('mousedown', () => raisePane(panel), { capture: true });

    const fireToggle = (): void => {
        panel.classList.toggle('collapsed');
        const collapsedNow = panel.classList.contains('collapsed');
        if (onAfterResize) setTimeout(onAfterResize, 300);
        if (!collapsedNow) raisePane(panel);
        // Resolve any overlap a fresh collapse creates (CSS aligns docked
        // panes to top:15). Wait the 0.3s collapse transition out.
        else setTimeout(() => resolveCollapsedOverlap(panel), 310);
        if (onToggle) onToggle(collapsedNow);
    };

    if (!opts.disableCollapse) {
        handle.addEventListener('click', (e) => {
            if (drag.wasDragging()) return;
            const target = e.target as Element | null;
            if (target?.closest('.pane-toggle, .collapse-btn, button, a, [data-no-drag]')) return;
            e.stopPropagation();
            fireToggle();
        });

        // Scope past `.colorbar-settings-btn` — the colorbar gear also carries
        // `.pane-toggle`, and now that the colorbar chevron is gone it would
        // otherwise become the first match and wire collapse onto the gear.
        const toggleBtn = panel.querySelector('.pane-toggle:not(.colorbar-settings-btn)') as HTMLElement | null;
        if (toggleBtn) {
            toggleBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                if (drag.wasDragging()) return;
                fireToggle();
            });
        }
    }

    if (onEdgeChange) {
        panel.addEventListener('paneedgechange', (e) => {
            const detail = (e as CustomEvent).detail as { edge: PaneEdge };
            onEdgeChange(detail.edge);
        });
    }

    return drag;
}

/** Panes wired by the standard click+drag setup. */
const STANDARD_PANES: ReadonlyArray<{ id: string; selfHandle?: true; disableCollapse?: true }> = [
    // Colorbar has no header — the whole pane is the drag surface, and the
    // `paneedgechange` event triggers a canvas repaint in colorbar.ts.
    { id: 'colorbar-container', selfHandle: true },
];

/**
 * Wire collapse + drag handlers for Controls, Colorbar.
 * `onWindowResize` is the post-collapse renderer resize callback.
 */
export function setupPanelCollapse(_viewer: ViewerContext, opts: { onWindowResize?: (() => void) | undefined } = {}): void {
    const { onWindowResize } = opts;
    const draggables: HTMLElement[] = [];

    for (const { id, selfHandle, disableCollapse } of STANDARD_PANES) {
        const panel = document.getElementById(id);
        if (!panel) continue;
        setupPane({
            panel,
            ...(selfHandle ? { dragHandle: panel } : {}),
            ...(disableCollapse ? { disableCollapse: true } : {}),
            onAfterResize: onWindowResize,
        });
        draggables.push(panel);
    }

    if (draggables.length) installResizeClamp(draggables);
}

// `controls` is intentionally absent — the Controls rail/flyout owns the
// UI_PANEL_TOGGLE { which: 'controls' } intent directly (see tabs.ts).
const PANEL_ID_BY_WHICH: Partial<Record<Which, string>> = {
    colorbar: 'colorbar-container',
};

function toggleByWhich(which: Which, opts: ToggleOpts): void {
    const id = PANEL_ID_BY_WHICH[which];
    if (!id) return;
    const panel = document.getElementById(id);
    if (panel) togglePanel(panel, opts);
}

function cyclePanelsVisibility(opts: ToggleOpts): void {
    const ids = ['colorbar-container'];
    const panels = ids.map((id) => document.getElementById(id)).filter((p): p is HTMLElement => !!p);
    const hideAll = panels.some((p) => !p.classList.contains('collapsed'));
    panels.forEach((panel) => panel.classList.toggle('collapsed', hideAll));
    if (opts && typeof opts.onAfterResize === 'function') setTimeout(opts.onAfterResize, 300);
}

export function installPanelsDriver(store: Store<State>, viewer: ViewerContext, opts: { onWindowResize?: (() => void) | undefined } = {}): () => void {
    const { onWindowResize } = opts;
    setupPanelCollapse(viewer, { onWindowResize });
    const toggleOpts: ToggleOpts = { onAfterResize: onWindowResize };
    const offs = [
        store.onIntent(INTENTS.UI_PANEL_TOGGLE, (p) => {
            if (p && typeof p.which === 'string') toggleByWhich(p.which as Which, toggleOpts);
        }),
        store.onIntent(INTENTS.UI_PANEL_CYCLE, () => cyclePanelsVisibility(toggleOpts)),
    ];
    return () => offs.forEach((off) => off());
}
